import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  CalendarIcon,
  NewspaperIcon
} from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import { addActivity } from '../services/activityService';
import apiService from '../services/apiService';
import { getImageUrlWithEnv } from '../utils/imageUrl';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      setLoading(true);
      const newsData = await apiService.getNews();
      setNews(newsData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading news:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const newsItem = news.find(item => item.id === id);
    if (window.confirm('ნამდვილად გსურთ ამ ამბის წაშლა?')) {
      try {
        await apiService.deleteNews(id);
        if (newsItem) {
          addActivity('news', 'deleted', {
            title: newsItem.title,
            description: `ამბავი "${newsItem.title}" წაიშალა`
          });
        }
        loadNews();
      } catch (error) {
        console.error('Error deleting news:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ka-GE');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AdminHeader 
        title="ამბების მართვა"
        subtitle="მართეთ საიტის ამბები"
      >
        <button
          onClick={() => navigate('/_panel-7c39f8/news/new')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium font-georgian flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          ახალი ამბავი
        </button>
      </AdminHeader>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {news.length === 0 ? (
            <div className="text-center py-12">
              <NewspaperIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 font-georgian">
                ამბები არ არის
              </h3>
              <p className="mt-1 text-sm text-gray-500 font-georgian">
                დაიწყეთ ახალი ამბის დამატებით
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/_panel-7c39f8/news/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 font-georgian"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  ახალი ამბავი
                </button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {news.map((article) => (
                <li key={article.id}>
                  <div className="px-4 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-16 w-16">
                        {article.image ? (
                          <img
                            className="h-16 w-16 rounded-lg object-cover"
                            src={getImageUrlWithEnv(article.image)}
                            alt={article.title}
                          />
                        ) : (
                          <div className="h-16 w-16 bg-gray-200 rounded-lg flex items-center justify-center">
                            <NewspaperIcon className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-gray-900 font-georgian">
                            {article.title}
                          </h3>
                          <div className="flex items-center space-x-2">
                            {article.published ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 font-georgian">
                                გამოქვეყნებული
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 font-georgian">
                                მონახაზი
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="mt-1 text-sm text-gray-500 font-georgian line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                          <span className="font-georgian">{formatDate(article.date)}</span>
                          <span className="mx-2">•</span>
                          <span className="font-georgian">{article.category}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/_panel-7c39f8/news/${article.id}`)}
                        className="text-primary-600 hover:text-primary-900 p-2"
                        title="რედაქტირება"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(article.id)}
                        className="text-red-600 hover:text-red-900 p-2"
                        title="წაშლა"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsManagement;
